import React, { useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
} from "@mui/material";
import TableLoader from "./TableLoaderReusable";
import { useTheme } from "@mui/material/styles";
import { useRecoilState } from "recoil";
import { selectedStreamState } from "../../Recoil/RecoilState";

const TableReusable = ({ data, columns, loading, selected, handelChangeSelect, showCheckbox }) => {
  const theme = useTheme();
  const [selectedData, setSelectedData] = useRecoilState(selectedStreamState);

  if (loading) return <TableLoader columns={columns.length || 12} />;

  const isSelected = (row) => {
    return selectedData.some((item) => item.id === row.id);
  };

  const handleCheckboxChange = (row) => {
    if (handelChangeSelect) {
      // Use parent handler if provided
      handelChangeSelect(row);
    } else {
      // Fallback to local logic
      if (isSelected(row)) {
        setSelectedData(selectedData.filter((item) => item.id !== row.id));
      } else {
        setSelectedData([...selectedData, row]);
      }
    }
  };

  // Check if all rows are selected
  const isAllSelected = () => {
    if (!data || data.length === 0) return false;
    return data.every(row => isSelected(row));
  };

  // Check if some rows are selected (for indeterminate state)
  const isSomeSelected = () => {
    if (!data || data.length === 0) return false;
    return data.some(row => isSelected(row)) && !isAllSelected();
  };

  // Handle select all/deselect all
  const handleSelectAll = () => {
    if (!data) return;
    
    if (isAllSelected()) {
      // Deselect all - remove all current page items from selection
      const currentPageIds = data.map(row => row.id);
      const newSelection = selectedData.filter(item => !currentPageIds.includes(item.id));
      setSelectedData(newSelection);
    } else {
      // Select all - add all current page items that aren't already selected
      const currentPageIds = data.map(row => row.id);
      const newItems = data.filter(row => !selectedData.some(item => item.id === row.id));
      setSelectedData([...selectedData, ...newItems]);
    }
  };

  // Determine which checkbox prop to use (backward compatibility)
  const showSelectionCheckbox = selected || showCheckbox;

  return (
    <TableContainer
      component={Paper}
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "4px",
        overflow: "auto",
      }}
    >
      <Table sx={{ minWidth: 500 }}>
        {/* Table Header */}
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.palette.background.default }}>
            {showSelectionCheckbox && (
              <TableCell 
                align="center" 
                sx={{ 
                  fontWeight: "bold",
                  width: "60px",
                  borderBottom: `2px solid ${theme.palette.divider}`,
                }}
              >
                <Checkbox
                  indeterminate={isSomeSelected()}
                  checked={isAllSelected()}
                  onChange={handleSelectAll}
                  disabled={!data || data.length === 0}
                  sx={{
                    color: theme.palette.primary.main,
                    '&.Mui-checked': {
                      color: theme.palette.primary.main,
                    },
                    '&.MuiCheckbox-indeterminate': {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
              </TableCell>
            )}
            {columns.map((col) => (
              <TableCell
                key={col.field}
                align="center"
                sx={{
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  fontSize: "14px",
                  letterSpacing: "0.8px",
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  color: theme.palette.text.primary,
                }}
              >
                {col.headerName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {/* Table Body */}
        <TableBody>
          {data?.length > 0 ? (
            data?.map((row, rowIndex) => (
              <TableRow
                key={row.id || rowIndex}
                sx={{
                 
                
                  transition: 'background-color 0.2s ease',
                }}
              >
                {showSelectionCheckbox && (
                  <TableCell 
                    align="center"
                    sx={{ 
                      width: "60px",
                      padding: "8px",
                    }}
                  >
                    <Checkbox
                      checked={isSelected(row)}
                      onChange={() => handleCheckboxChange(row)}
                      sx={{
                        color: theme.palette.primary.main,
                        '&.Mui-checked': {
                          color: theme.palette.primary.main,
                        },
                      }}
                    />
                  </TableCell>
                )}

                {columns.map((col) => (
                  <TableCell
                    key={col.field}
                    align="center"
                    sx={{ 
                      color: theme.palette.text.primary,
                      "&:hover": {
                        color: theme.palette.text.secondary
                      },
                      padding: "12px 8px",
                    }}
                  >
                
                    {col.field === "frame" ? (
                      <img
                        src={`data:image/jpeg;base64,${row[col.field]}`}
                        alt="Frame"
                        width="50"
                        style={{ 
                          borderRadius: "4px",
                          objectFit: "cover" 
                        }}
                      />
                    ) : (
                      row[col.field] || row.metadata?.[col.field] || "—"
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length + (showSelectionCheckbox ? 1 : 0)}
                align="center"
                sx={{ 
                  color: theme.palette.text.secondary,
                  padding: "24px",
                  fontSize: "16px"
                }}
              >
                No data available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      
      {/* Selection Summary */}
      {showSelectionCheckbox && selectedData.length > 0 && (
        <div style={{
          padding: "8px 16px",
          backgroundColor: theme.palette.primary.light,
          color: theme.palette.primary.contrastText,
          fontSize: "14px",
          textAlign: "center",
          borderTop: `1px solid ${theme.palette.divider}`
        }}>
          {selectedData.length} item{selectedData.length !== 1 ? 's' : ''} selected
        </div>
      )}
    </TableContainer>
  );
};

export default TableReusable;